<p-progressBar mode="indeterminate" class="m-2" *ngIf="isLoading"></p-progressBar>

<app-page-shell>
    <app-page-hero eyebrow="Reports" title="Trial Balance" subtitle="Validate debit and credit activity across the ledger.">
        <div actions class="report-actions">
            <p-button label="Generate" icon="pi pi-refresh" (onClick)="loadTrialBalanceData({})"></p-button>
            <p-button label="Export" icon="pi pi-file-excel" severity="success" (onClick)="exportExcel()"></p-button>
            <p-button label="Download PDF" icon="pi pi-file-pdf" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('download')" (onClick)="printReport(true)"></p-button>
            <p-button label="Print" icon="pi pi-print" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('print')" (onClick)="printReport()"></p-button>
            <app-report-email reportName="Trial Balance" [rows]="trialBalanceItems"></app-report-email>
        </div>
    </app-page-hero>

    <app-page-form-section icon="pi pi-filter" title="Report Filters" subtitle="Choose the trial balance date range.">
        <div class="report-filter-grid two-column">
            <p-iftalabel>
                <p-datePicker inputId="startDate" [(ngModel)]="startDate" dateFormat="yy-mm-dd" (onSelect)="loadTrialBalanceData({})" [showIcon]="true" styleClass="w-full"></p-datePicker>
                <label for="startDate">Start Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-datePicker inputId="endDate" [(ngModel)]="endDate" dateFormat="yy-mm-dd" (onSelect)="loadTrialBalanceData({})" [showIcon]="true" styleClass="w-full"></p-datePicker>
                <label for="endDate">End Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <app-logistics-project-lookup inputId="logisticsProjectId" [(ngModel)]="selectedLogisticsProjectId" (ngModelChange)="loadTrialBalanceData({})"></app-logistics-project-lookup>
                <label for="logisticsProjectId">Logistics Project</label>
            </p-iftalabel>
        </div>
    </app-page-form-section>

<app-page-data-panel *ngIf="diagnostics.length" title="Report Diagnostics" [resultText]="diagnostics.length + ' checks'">
    <p-table [value]="diagnostics" styleClass="p-datatable-gridlines" [tableStyle]="{ 'min-width': '60rem' }">
        <ng-template pTemplate="header">
            <tr>
                <th>Severity</th>
                <th>Code</th>
                <th>Message</th>
                <th>Reference</th>
                <th class="text-right">Amount</th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-diagnostic>
            <tr>
                <td>{{ diagnostic.severity }}</td>
                <td>{{ diagnostic.code }}</td>
                <td>{{ diagnostic.message }}</td>
                <td>{{ diagnostic.referenceNumber || diagnostic.referenceId || '-' }}</td>
                <td class="text-right">{{ diagnostic.amount ?? '-' }}</td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>

<!-- Summary Cards for Totals (Total Debits, Total Credits) -->
<div class="grid">
    <div class="col-12 md:col-6">
        <p-card header="Total Debits" styleClass="text-center">
            <div class="text-2xl font-bold">
                {{ totalDebitsSum | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
            </div>
        </p-card>
    </div>
    <div class="col-12 md:col-6">
        <p-card header="Total Credits" styleClass="text-center">
            <div class="text-2xl font-bold">
                {{ totalCreditsSum | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
            </div>
        </p-card>
    </div>
    <!-- A trial balance should always have Total Debits == Total Credits -->
    <div class="col-12 text-center mt-2">
        <p-card header="Net Difference (Debits - Credits)" styleClass="text-center" [ngClass]="{'bg-red-100': netDifference !== 0, 'bg-green-100': netDifference === 0}">
            <div class="text-2xl font-bold" [ngClass]="{'text-red-600': netDifference !== 0, 'text-green-600': netDifference === 0}">
                {{ netDifference | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
            </div>
            <div *ngIf="netDifference !== 0" class="text-sm text-red-500">
                Trial balance is not balanced! There's a discrepancy.
            </div>
        </p-card>
    </div>
</div>

<app-page-data-panel title="Trial Balance Report" [resultText]="(totalRecords ? totalRecords : 0) + ' entries'">
<p-table [value]="visibleTrialBalanceItems" dataKey="accountCode" [resizableColumns]="true" styleClass="accounting-tree-table" [tableStyle]="{ 'min-width': '85rem' }"
         [paginator]="true" [rows]="20" [first]="first" [showCurrentPageReport]="true"
         currentPageReportTemplate="Showing {first} to {last} of {totalRecords} accounts"
         [rowsPerPageOptions]="[20, 50, 100]">

    <ng-template pTemplate="header">
        <tr>
            <th class="report-account-column">Account</th>
            <th>Type</th>
            <th class="text-right">Opening</th>
            <th class="text-right">Period Debit</th>
            <th class="text-right">Period Credit</th>
            <th class="text-right">Closing Debit</th>
            <th class="text-right">Closing Credit</th>
            <th class="text-right">Closing Balance</th>
        </tr>
    </ng-template>

    <ng-template pTemplate="body" let-item>
        <tr [ngClass]="{'report-summary-row': item.isSummaryRow, 'report-root-row': item.level === 0, 'report-direct-row': item.isDirectPostingRow}">
            <td>
                <app-accounting-report-tree-cell [accountCode]="item.accountCode" [accountName]="item.accountName"
                    [level]="item.level" [childCount]="item.children?.length || 0" [expanded]="!!item.expanded"
                    [summary]="!!item.isSummaryRow" [direct]="!!item.isDirectPostingRow" directLabel="Direct activity"
                    (toggled)="toggleNode(item)"></app-accounting-report-tree-cell>
            </td>
            <td><span class="account-type-badge">{{ item.internalType | titlecase }}</span></td>
            <td class="report-value-cell" [ngClass]="{'report-value-credit': getBalanceSide(item, item.openingBalance) === 'CR', 'report-value-zero': getBalanceSide(item, item.openingBalance) === 'ZERO'}">
                <div class="report-value-stack"><span class="report-value">{{ item.openingBalance | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</span><span class="report-balance-side">{{ getBalanceSide(item, item.openingBalance) }}</span></div>
            </td>
            <td class="report-value-cell" [class.report-value-zero]="!item.totalDebit">
                <div class="report-value-stack"><span class="report-value">{{ item.totalDebit | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</span><span class="report-balance-side">{{ item.totalDebit ? 'DR' : 'ZERO' }}</span></div>
            </td>
            <td class="report-value-cell report-value-credit" [class.report-value-zero]="!item.totalCredit">
                <div class="report-value-stack"><span class="report-value">{{ item.totalCredit | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</span><span class="report-balance-side">{{ item.totalCredit ? 'CR' : 'ZERO' }}</span></div>
            </td>
            <td class="report-value-cell" [class.report-value-zero]="!item.closingDebit">
                <div class="report-value-stack"><span class="report-value">{{ item.closingDebit | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</span><span class="report-balance-side">{{ item.closingDebit ? 'DR' : 'ZERO' }}</span></div>
            </td>
            <td class="report-value-cell report-value-credit" [class.report-value-zero]="!item.closingCredit">
                <div class="report-value-stack"><span class="report-value">{{ item.closingCredit | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</span><span class="report-balance-side">{{ item.closingCredit ? 'CR' : 'ZERO' }}</span></div>
            </td>
            <td class="report-value-cell" [ngClass]="{'report-value-credit': getBalanceSide(item, item.closingBalance) === 'CR', 'report-value-zero': getBalanceSide(item, item.closingBalance) === 'ZERO'}">
                <div class="report-value-stack"><span class="report-value">{{ item.closingBalance | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</span><span class="report-balance-side">{{ getBalanceSide(item, item.closingBalance) }}</span></div>
            </td>
        </tr>
    </ng-template>

    <ng-template pTemplate="footer">
        <tr>
            <td colspan="5" class="font-bold text-right">Closing Totals:</td>
            <td class="text-right font-bold">{{ totalDebitsSum | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
            <td class="text-right font-bold">{{ totalCreditsSum | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}</td>
            <td class="text-right font-bold" [ngClass]="{'text-red-600': netDifference !== 0, 'text-green-600': netDifference === 0}">
                {{ netDifference | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
            </td>
        </tr>
    </ng-template>

    <ng-template pTemplate="summary">
        <div class="flex align-items-center justify-content-between">
            Showing {{ totalRecords ? totalRecords : 0 }} visible accounts in the chart hierarchy.
        </div>
    </ng-template>
</p-table>
</app-page-data-panel>
</app-page-shell>
